import { useState } from "react";
import { Button } from "../../components/ui/button";
import { X, Mail } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useToast } from "../../hooks/use-toast";
import { Label } from "@radix-ui/react-label";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { useDispatch } from "react-redux";
import { SendResetPasswordLinkAPI } from "./authAPI";
import { CheckCircle, AlertCircle } from "lucide-react";

const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [localFormError, setLocalFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const intl = useIntl();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setLocalFormError(null);
    setSuccessMessage(null);

    if (!email.trim()) {
      setLocalFormError(
        intl.formatMessage({
          id: "forgotPassword.emailRequired",
          defaultMessage: "Please enter your email address",
        })
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setLocalFormError(
        intl.formatMessage({
          id: "forgotPassword.invalidEmail",
          defaultMessage: "Please enter a valid email address",
        })
      );
      return;
    }

    setIsLoading(true);

    try {
      await dispatch(SendResetPasswordLinkAPI(email.trim()));

      // Show success message in modal
      setSuccessMessage(
        intl.formatMessage({
          id: "forgotPassword.successDesc",
          defaultMessage:
            "Password reset link has been sent to your email address",
        })
      );

      // Close modal after 2 seconds to let user see success message
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      // Show error message in modal (NOT toast)
      setLocalFormError(
        error.message ||
          intl.formatMessage({
            id: "forgotPassword.failedDesc",
            defaultMessage:
              "Failed to send password reset link. Please try again.",
          })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="!p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              <FormattedMessage
                id="forgotPassword.title"
                defaultMessage="Forgot Password"
              />
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-1"
              disabled={isLoading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="text-center mb-4">
            <Mail className="w-12 h-12 mx-auto mb-3 text-blue-600" />
            <p className="text-gray-600 text-sm dark:text-gray-400">
              <FormattedMessage
                id="forgotPassword.instruction"
                defaultMessage="Enter your email address and we'll send you a link to reset your password."
              />
            </p>
          </div>

          {/* Error Message - Show in Modal */}
          {localFormError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-red-700 text-sm">{localFormError}</span>
            </div>
          )}

          {/* Success Message - Show in Modal */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-green-700 text-sm">{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">
                <FormattedMessage
                  id="forgotPassword.emailLabel"
                  defaultMessage="Email Address"
                />
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder={intl.formatMessage({
                    id: "forgotPassword.enterEmail",
                    defaultMessage: "Enter your email address",
                  })}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || successMessage}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                disabled={isLoading || successMessage}
                className="w-full"
              >
                {isLoading ? (
                  <FormattedMessage
                    id="forgotPassword.sending"
                    defaultMessage="Sending..."
                  />
                ) : (
                  <FormattedMessage
                    id="forgotPassword.sendLink"
                    defaultMessage="Send Reset Link"
                  />
                )}
              </Button>

              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="w-full"
                disabled={isLoading}
              >
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordModal;
